/*
 * Naturlabs content pages (nl-page-*, nl-b2b-*, nl-blog-*, nl-article-* sections). Vanilla, no dependencies, deferred.
 *
 * <nl-toc data-target="#id" data-levels="h2,h3">   table of contents generated from headings, scroll spy
 * <nl-validated-form>                               accessible client side validation for {% form %} markup
 * <nl-faq-filter>                                   live filter of FAQ questions, group scroll spy
 * [data-b2b-type="Lékárna"]                         preselects the business type in the B2B form and scrolls to it
 * [data-share-copy] / [data-share-native]           copy link / native share sheet
 * [data-print]                                      print the page
 *
 * Everything degrades gracefully without JS (TOC hidden, native validation, plain anchors).
 */
(() => {
  'use strict';

  /* Several sections on one page include this file (classic scripts run once per tag): bind everything once */
  if (window.NLPage) return;
  window.NLPage = true;

  const NL = (window.NL = window.NL || {});
  const reduced = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.classList.contains('nl-motion-off');
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  const toast = (message, variant) => {
    if (!message) return;
    if (typeof NL.toast === 'function') NL.toast(message, { variant: variant || 'success' });
  };

  const slugify = (text) =>
    String(text)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'sekce';

  /* Scroll spy shared by the TOC and the FAQ group navigation: the active target is the last one whose top
     edge has passed 30 % of the viewport height (rAF throttled, passive listener). */
  const spy = (targets, onChange) => {
    if (!targets.length) return null;
    let ticking = false;
    let current = null;
    const update = () => {
      ticking = false;
      const line = window.innerHeight * 0.3;
      let active = null;
      for (const target of targets) {
        if (target.hidden || target.closest('[hidden]')) continue;
        if (!active) active = target;
        if (target.getBoundingClientRect().top - line <= 0) active = target;
        else break;
      }
      if (active && active !== current) {
        current = active;
        onChange(active);
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
    return {
      disconnect: () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      },
    };
  };

  /* ==========================================================================
     Table of contents
     ========================================================================== */

  class NLToc extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      const source = document.querySelector(this.dataset.target || '');
      const list = this.querySelector('[data-toc-list]');
      if (!source || !list) return;
      const levels = (this.dataset.levels || 'h2').split(',').map((s) => s.trim().toLowerCase());
      const headings = Array.from(source.querySelectorAll(levels.join(','))).filter((h) => h.textContent.trim());
      if (headings.length < 2) return;
      this._bound = true;

      const used = new Set(Array.from(document.querySelectorAll('[id]')).map((el) => el.id));
      const frag = document.createDocumentFragment();
      this.links = headings.map((heading) => {
        if (!heading.id) {
          let id = slugify(heading.textContent);
          let n = 2;
          while (used.has(id)) id = `${slugify(heading.textContent)}-${n++}`;
          used.add(id);
          heading.id = id;
        }
        heading.setAttribute('tabindex', '-1');
        const li = document.createElement('li');
        if (heading.tagName.toLowerCase() !== levels[0]) li.className = 'is-sub';
        const a = document.createElement('a');
        a.href = `#${heading.id}`;
        a.textContent = heading.textContent.trim();
        li.appendChild(a);
        frag.appendChild(li);
        return a;
      });
      list.appendChild(frag);
      this.hidden = false;

      this.details = this.querySelector('details');
      const desktop = window.matchMedia('(min-width: 1024px)');
      const syncOpen = () => {
        if (this.details && desktop.matches) this.details.open = true;
      };
      syncOpen();
      desktop.addEventListener('change', syncOpen);

      list.addEventListener('click', (event) => {
        const link = event.target.closest('a[href^="#"]');
        if (!link) return;
        const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        target.focus({ preventScroll: true });
        history.replaceState(null, '', link.hash);
        if (this.details && !desktop.matches) this.details.open = false;
      });

      this.observer = spy(headings, (active) => {
        this.links.forEach((a) => {
          if (a.hash === `#${active.id}`) a.setAttribute('aria-current', 'true');
          else a.removeAttribute('aria-current');
        });
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  define('nl-toc', NLToc);

  /* ==========================================================================
     Accessible form validation (wraps a Shopify {% form %})
     ========================================================================== */

  class NLValidatedForm extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this._bound = true;
      this.form.setAttribute('novalidate', '');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.form.addEventListener('input', (event) => this.onInput(event));
      this.form.addEventListener('change', (event) => this.onInput(event));
      window.addEventListener('pageshow', () => this.resetSubmit());

      // Only the B2B form carries its extra fields through Shopify's documented form.body response.
      // No draft is stored in browser storage, a cookie or the URL.
      if (this.hasAttribute('data-b2b-retain-body')) {
        this.form.addEventListener('formdata', (event) => this.prepareB2bBody(event.formData));
        if (this.querySelector('[data-form-server-error]')) this.restoreB2bBody();
      }
    }

    b2bFields() {
      return [
        ['contact[Firma]', 'Firma'],
        ['contact[IČO]', 'IČO'],
        ['contact[name]', 'Kontaktní osoba'],
        ['contact[Typ provozu]', 'Typ provozu'],
        ['contact[phone]', 'Telefon'],
        ['contact[Město]', 'Město'],
      ];
    }

    prepareB2bBody(data) {
      const message = data.get('contact[body]');
      const values = this.b2bFields().map(([name]) => data.get(name));
      // Keep unexpectedly large or non-string payloads untouched rather than truncate an inquiry.
      if (typeof message !== 'string' || message.length > 100000) return;
      if (values.some((value) => typeof value !== 'string' || value.length > 4096 || /[\r\n]/.test(value))) return;
      const lines = this.b2bFields().map(([, label], index) => `${label}: ${JSON.stringify(values[index])}`);
      data.set('contact[body]', `[NaturLabs B2B v1]\n${lines.join('\n')}\n[/NaturLabs B2B v1]\n\n${message}`);
    }

    restoreB2bBody() {
      const message = this.form.elements.namedItem('contact[body]');
      if (!message || typeof message.value !== 'string' || message.value.length > 260000) return;
      const start = '[NaturLabs B2B v1]\n';
      const end = '\n[/NaturLabs B2B v1]\n\n';
      const body = message.value;
      if (!body.startsWith(start)) return;
      const boundary = body.indexOf(end, start.length);
      if (boundary < 0) return;
      const fields = this.b2bFields();
      const lines = body.slice(start.length, boundary).split('\n');
      const originalMessage = body.slice(boundary + end.length);
      if (lines.length !== fields.length || originalMessage.length > 100000) return;
      const restored = [];
      try {
        fields.forEach(([name, label], index) => {
          const prefix = `${label}: `;
          if (!lines[index].startsWith(prefix)) throw new Error('Unexpected B2B field');
          const value = JSON.parse(lines[index].slice(prefix.length));
          const control = this.form.elements.namedItem(name);
          if (typeof value !== 'string' || value.length > 4096 || /[\r\n]/.test(value) || !control) {
            throw new Error('Invalid B2B field value');
          }
          if (control.tagName === 'SELECT' && !Array.from(control.options).some((option) => option.value === value)) {
            throw new Error('Unknown B2B business type');
          }
          restored.push([control, value]);
        });
      } catch (error) {
        // A malformed or unrecognised body stays visible and unchanged for manual recovery.
        return;
      }
      // Assign values only after validating the entire fixed field list. Never interpret returned HTML.
      restored.forEach(([control, value]) => { control.value = value; });
      message.value = originalMessage;
      // Consent deliberately remains unchecked and is never encoded in this envelope.
    }

    resetSubmit() {
      window.clearTimeout(this._submitCancelCheck);
      this._submitEvent = null;
      this._submitting = false;
      this.form.removeAttribute('aria-busy');
      (this._submitButtons || []).forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
      this._submitButtons = [];
    }

    message(field) {
      const v = field.validity;
      const d = this.dataset;
      if (v.valueMissing) return field.type === 'checkbox' ? d.msgConsent || d.msgRequired : d.msgRequired;
      if (v.typeMismatch && field.type === 'email') return d.msgEmail;
      if (v.patternMismatch || v.typeMismatch) return field.dataset.msgPattern || (field.type === 'tel' ? d.msgPhone : d.msgRequired);
      return field.validationMessage;
    }

    errorEl(field) {
      const id = `${field.id}-error`;
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('p');
        el.id = id;
        el.className = 'nl-field__error';
        const holder = field.closest('.nl-field') || field.parentElement;
        holder.appendChild(el);
      }
      return el;
    }

    setError(field, text) {
      const el = this.errorEl(field);
      let describedBy = (field.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
      if (text) {
        el.textContent = text;
        el.hidden = false;
        field.setAttribute('aria-invalid', 'true');
        if (!describedBy.includes(el.id)) describedBy.push(el.id);
      } else {
        el.textContent = '';
        el.hidden = true;
        field.removeAttribute('aria-invalid');
        describedBy = describedBy.filter((id) => id !== el.id);
      }
      if (describedBy.length) field.setAttribute('aria-describedby', describedBy.join(' '));
      else field.removeAttribute('aria-describedby');
    }

    renderSummary(invalid) {
      const summary = this.querySelector('[data-form-summary]');
      if (!summary) return;
      const list = summary.querySelector('ul');
      if (list) {
        list.replaceChildren();
        invalid.forEach((field) => {
          const label = this.form.querySelector(`label[for="${CSS.escape(field.id)}"]`);
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = `#${field.id}`;
          a.textContent = label ? label.textContent.replace(/\*/g, '').trim() : field.name;
          a.addEventListener('click', (event) => {
            event.preventDefault();
            field.focus();
          });
          li.appendChild(a);
          list.appendChild(li);
        });
      }
      summary.hidden = invalid.length === 0;
    }

    onInput(event) {
      const field = event.target;
      if (!field.matches || !field.matches('input, select, textarea')) return;
      if (field.getAttribute('aria-invalid') === 'true' && field.checkValidity()) {
        this.setError(field, '');
        this.renderSummary(Array.from(this.form.elements).filter((el) => el.getAttribute('aria-invalid') === 'true'));
      }
    }

    onSubmit(event) {
      // A provider may cancel one attempt and immediately resume with a new submit event.
      if (this._submitting && this._submitEvent && this._submitEvent.defaultPrevented) this.resetSubmit();
      if (this._submitting) {
        event.preventDefault();
        return;
      }
      const fields = Array.from(this.form.elements).filter(
        (el) => el.matches('input, select, textarea') && el.type !== 'hidden' && !el.disabled && el.willValidate
      );
      const invalid = fields.filter((field) => {
        const isB2bMessage = this.hasAttribute('data-b2b-retain-body') && field.name === 'contact[body]';
        if (field.value && field.type !== 'checkbox' && field.type !== 'radio' && !isB2bMessage) field.value = field.value.trim();
        const ok = field.checkValidity();
        this.setError(field, ok ? '' : this.message(field));
        return !ok;
      });

      this.renderSummary(invalid);
      if (invalid.length) {
        event.preventDefault();
        invalid[0].focus();
        return;
      }

      this._submitting = true;
      this._submitEvent = event;
      this.form.setAttribute('aria-busy', 'true');
      this._submitButtons = Array.from(this.form.querySelectorAll('button[type="submit"], input[type="submit"]')).filter(
        (button) => !button.disabled
      );
      this._submitButtons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });
      // Shopify CAPTCHA may defer the native submission. Let its handler resume or retry normally.
      // An accepted native submission stays locked until a fresh document or a history return.
      // Native event dispatch can run microtasks between listeners. Wait for the next task,
      // then release only this cancelled attempt, never a newer resumed submission.
      this._submitCancelCheck = window.setTimeout(() => {
        if (this._submitEvent === event && event.defaultPrevented) this.resetSubmit();
      }, 0);
    }
  }

  define('nl-validated-form', NLValidatedForm);

  /* ==========================================================================
     FAQ live filter + group navigation
     ========================================================================== */

  class NLFaqFilter extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = this.querySelector('[data-faq-input]');
      this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
      this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
      this.status = this.querySelector('[data-faq-status]');
      this.empty = this.querySelector('[data-faq-empty]');
      this.navLinks = Array.from(this.querySelectorAll('[data-faq-nav] a'));
      this.initiallyOpen = new Set(this.items.filter((item) => item.querySelector('details[open]')));

      if (this.input) {
        const run = NL.debounce ? NL.debounce(() => this.filter(), 140) : () => this.filter();
        this.input.addEventListener('input', run);
        this.input.addEventListener('search', run);
      }

      if (this.navLinks.length) {
        this.navLinks.forEach((link) =>
          link.addEventListener('click', (event) => {
            const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
            if (!target) return;
            event.preventDefault();
            target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
            const heading = target.querySelector('h2, h3');
            if (heading) heading.focus({ preventScroll: true });
            history.replaceState(null, '', link.hash);
          })
        );
        this.observer = spy(this.groups, (active) => {
          this.navLinks.forEach((a) => {
            if (a.hash === `#${active.id}`) a.setAttribute('aria-current', 'true');
            else a.removeAttribute('aria-current');
          });
        });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    normalize(text) {
      return String(text).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    }

    filter() {
      const query = this.normalize(this.input.value.trim());
      let count = 0;
      this.items.forEach((item) => {
        if (!item._text) item._text = this.normalize(item.textContent);
        const match = !query || item._text.includes(query);
        item.hidden = !match;
        if (match) count += 1;
        const details = item.querySelector('details');
        if (details) {
          if (query.length >= 3 && match) details.open = true;
          else if (!query) details.open = this.initiallyOpen.has(item);
        }
      });
      this.groups.forEach((group) => {
        group.hidden = !group.querySelector('[data-faq-item]:not([hidden])');
      });
      this.navLinks.forEach((link) => {
        const group = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (link.parentElement) link.parentElement.hidden = Boolean(group && group.hidden);
      });
      if (this.empty) this.empty.hidden = count > 0;
      if (this.status) {
        if (!query) {
          this.status.textContent = '';
        } else {
          const strings = (NL.strings && NL.strings.searchResults) || {};
          let key = 'other';
          try {
            key = new Intl.PluralRules(document.documentElement.lang || 'cs').select(count);
          } catch (e) {
            key = count === 1 ? 'one' : 'other';
          }
          const template = strings[key] || strings.other || '%count%';
          this.status.textContent = template.replace('%count%', count);
        }
      }
    }
  }

  define('nl-faq-filter', NLFaqFilter);

  /* ==========================================================================
     Delegated behaviours
     ========================================================================== */

  document.addEventListener('click', async (event) => {
    /* B2B: segment card preselects the business type */
    const typeTrigger = event.target.closest('[data-b2b-type]');
    if (typeTrigger) {
      const hash = typeTrigger.hash || '';
      const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
      const formSection = target && (target.closest('.nl-b2b-form') || target);
      const select = formSection && formSection.querySelector('[data-b2b-type-select]');
      if (select) {
        const wanted = typeTrigger.dataset.b2bType.trim().toLowerCase();
        const option = Array.from(select.options).find(
          (o) => o.value.trim().toLowerCase() === wanted || o.textContent.trim().toLowerCase() === wanted
        );
        if (option) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', hash);
        const card = formSection.querySelector('.nl-page-formcard');
        const first = formSection.querySelector('form input:not([type="hidden"]), form select, form textarea');
        window.setTimeout(
          () => {
            if (first) first.focus({ preventScroll: true });
            if (card && !reduced()) {
              card.classList.remove('is-flash');
              void card.offsetWidth;
              card.classList.add('is-flash');
            }
          },
          reduced() ? 0 : 600
        );
      }
      return;
    }

    /* Share: copy link */
    const copy = event.target.closest('[data-share-copy]');
    if (copy) {
      event.preventDefault();
      const url = copy.dataset.url || window.location.href;
      try {
        await navigator.clipboard.writeText(url);
        toast(copy.dataset.copied);
      } catch (e) {
        window.prompt(copy.dataset.label || '', url);
      }
      return;
    }

    /* Share: native sheet */
    const native = event.target.closest('[data-share-native]');
    if (native && navigator.share) {
      event.preventDefault();
      try {
        await navigator.share({ title: native.dataset.title || document.title, url: native.dataset.url || window.location.href });
      } catch (e) {
        /* dismissed */
      }
      return;
    }

    /* Print */
    if (event.target.closest('[data-print]')) {
      event.preventDefault();
      window.print();
    }
  });

  /* Reveal native share buttons only where supported */
  const showNative = (root) => {
    if (!navigator.share) return;
    root.querySelectorAll('[data-share-native][hidden]').forEach((el) => {
      el.hidden = false;
    });
  };
  showNative(document);
  document.addEventListener('shopify:section:load', (event) => showNative(event.target));
})();
